/**
 * What to do with an email a supplier sent to orders@ (ADR 0311).
 *
 * Jonathon, 2026-10-26: "any email sent to orders@copperridge-roofing.com
 * (group email that has me, sam, and lysa) goes to that customer's profile in
 * job docs and is properly named and sorted. it will be order acknowledgements
 * (when the supplier first receives an order), invoice (when it's picked up or
 * delivered), and credit memo's when an order is returned."
 *
 * Three decisions, all pure, all made here and nowhere else (rule 5):
 *
 *   1. **What kind of document it is** — acknowledgement, invoice, credit
 *      memo, or unknown. Read off the subject first, then the attachment
 *      names, then the new text of the body. An acknowledgement is what a
 *      supplier sends when the order lands and it confirms nothing about the
 *      prices, so it must never be filed as an invoice: the two words are
 *      looked for separately and the acknowledgement wins a tie.
 *   2. **Which order it is about** — our MO number, wherever the supplier
 *      echoed it. Only the new text of a reply counts: the quoted history
 *      under it names every order the thread has ever mentioned.
 *   3. **What the file is called** — one shape for every document, so Docs
 *      reads "MO-900095 · Invoice · ABC Supply · 2026-10-26" for the thing a
 *      person would otherwise have to open to find out.
 *
 * Matching is deliberately binary. One existing order named: file it. None,
 * two, or a number that is not an order: review. Nothing here guesses, and
 * `planSupplierMail` says why it would not.
 *
 * A fourth guards the plan: an email is filed on an order by itself only
 * when it came from that order's supplier (`confirmSupplierSender`).
 *
 * `supabase/functions/orders-inbox/filing.ts` carries a copy of everything
 * from `classifySupplierMail` here to `confirmSupplierSender` in
 * `supplierMailSender.ts`, because an Edge Function cannot import the workspace;
 * `supabase/test/orders-inbox-filing.test.ts` fails the build if the copy
 * drifts.
 */

import { stripQuotedReply } from "./emailThread.js";

/** What a supplier's email turned out to be. */
export type SupplierMailKind =
  "acknowledgement" | "invoice" | "credit_memo" | "unknown";

/** Where the kind was read from, most certain first. */
export type SupplierMailKindSource =
  "subject" | "attachment" | "body" | "reader" | "none";

export interface SupplierMailClassification {
  kind: SupplierMailKind;
  source: SupplierMailKindSource;
}

/** The `job_files.kind` each kind is filed under. */
export const SUPPLIER_MAIL_FILE_KIND: Record<SupplierMailKind, string> = {
  acknowledgement: "supplier_acknowledgement",
  invoice: "supplier_invoice",
  credit_memo: "supplier_credit_memo",
  unknown: "supplier_mail",
};

/** The word a person reads for each kind. */
export const SUPPLIER_MAIL_KIND_LABEL: Record<SupplierMailKind, string> = {
  acknowledgement: "Acknowledgement",
  invoice: "Invoice",
  credit_memo: "Credit memo",
  unknown: "Supplier email",
};

/**
 * The order the order screen lists them in: what the supplier sends first,
 * first. Unknown last, because it is the one somebody still has to look at.
 */
export const SUPPLIER_MAIL_KIND_ORDER: readonly SupplierMailKind[] = [
  "acknowledgement",
  "invoice",
  "credit_memo",
  "unknown",
];

// `_` as well as a space between words: an attachment is called
// "Order_Confirmation_55120.pdf", and an underscore is a word character to
// `\b`, so the edges are spelled out as "not a letter".
const EDGE = String.raw`(?<![A-Za-z])`;
const END = String.raw`(?![A-Za-z])`;
const GAP = String.raw`[\s_-]*`;
const CREDIT_MEMO = new RegExp(
  `${EDGE}credit${GAP}(?:memo|note|invoice)${END}|${EDGE}credit${GAP}#|${EDGE}RMA${END}|${EDGE}return(?:ed|s)?${GAP}(?:goods|authori[sz]ation|credit)${END}`,
  "i",
);
const ACKNOWLEDGEMENT = new RegExp(
  `${EDGE}acknowledg(?:e|ement|ment)${END}|${EDGE}order${GAP}confirmation${END}|${EDGE}confirmation${GAP}of${GAP}(?:your${GAP})?order${END}|${EDGE}sales${GAP}order${END}|${EDGE}received${GAP}your${GAP}(?:purchase${GAP})?order${END}|${EDGE}order${GAP}(?:has${GAP}been${GAP})?received${END}`,
  "i",
);
const INVOICE = new RegExp(`${EDGE}invoice${END}`, "i");

/**
 * What one piece of text says the document is, or null if it says nothing.
 *
 * Credit memo first: "credit invoice" is a memo, not a bill. Acknowledgement
 * before invoice: an acknowledgement often says "an invoice will follow", and
 * the thing in hand is still the acknowledgement.
 */
function kindOf(text: string | null | undefined): SupplierMailKind | null {
  const value = (text ?? "").trim();
  if (value === "") return null;
  if (CREDIT_MEMO.test(value)) return "credit_memo";
  if (ACKNOWLEDGEMENT.test(value)) return "acknowledgement";
  if (INVOICE.test(value)) return "invoice";
  return null;
}

export interface SupplierMailText {
  subject?: string | null;
  /** The whole body as received; quoted history is stripped here. */
  body?: string | null;
  attachmentNames?: readonly string[];
}

/**
 * What kind of document the email is, and where that was read from.
 *
 * The subject is the supplier's own one-line description and is trusted
 * first. An attachment name is nearly as good ("Invoice_4471.pdf"). The body
 * is last, and only its new text: a reply quoting "your invoice is attached"
 * from last week is not an invoice.
 */
export function classifySupplierMail(
  mail: SupplierMailText,
): SupplierMailClassification {
  const fromSubject = kindOf(mail.subject);
  if (fromSubject) return { kind: fromSubject, source: "subject" };

  for (const name of mail.attachmentNames ?? []) {
    const fromName = kindOf(name);
    if (fromName) return { kind: fromName, source: "attachment" };
  }

  const fromBody = kindOf(stripQuotedReply(mail.body));
  if (fromBody) return { kind: fromBody, source: "body" };

  return { kind: "unknown", source: "none" };
}

/**
 * Our order numbers, as a supplier echoes them.
 *
 * "MO-900095", "MO 900095", "MO#900095" and "mo900095" are all the same
 * order. A bare number is taken only after a purchase-order label — "PO
 * 900095", "Customer PO: 900095" — because a six-digit figure on its own is
 * as likely to be the supplier's invoice number. Order numbers come from a
 * sequence starting at 900000, so anything shorter than six digits is noise.
 */
const MO_PREFIXED = /\bMO\s*[-#:.]?\s*0*(\d{6,9})(?!\d)/gi;
const PO_LABELLED =
  /\b(?:customer\s+)?(?:P\.?O\.?|purchase\s+order|PO\s*(?:#|no\.?|number))\s*[-#:.]?\s*(?:MO\s*[-#:.]?\s*)?0*(\d{6,9})(?!\d)/gi;

export function orderNumbersIn(text: string | null | undefined): number[] {
  const value = text ?? "";
  const found = new Set<number>();
  for (const pattern of [MO_PREFIXED, PO_LABELLED]) {
    for (const match of value.matchAll(pattern)) {
      const number = Number(match[1]);
      if (Number.isFinite(number) && number > 0) found.add(number);
    }
  }
  return [...found].sort((a, b) => a - b);
}

/**
 * Every order number the email names in its own words: subject, attachment
 * names and the new text of the body, in that order. Quoted history is left
 * out for the reason `classifySupplierMail` gives.
 */
export function orderNumbersInMail(mail: SupplierMailText): number[] {
  const texts = [
    mail.subject ?? "",
    ...(mail.attachmentNames ?? []),
    stripQuotedReply(mail.body),
  ];
  return [...new Set(texts.flatMap(orderNumbersIn))].sort((a, b) => a - b);
}

export interface SupplierDocumentName {
  orderNumber: number | null;
  kind: SupplierMailKind;
  /** The supplier's name as the order knows it, or as the email signed it. */
  supplier: string | null;
  /** When the email was sent, ISO. Dated in the given time zone's calendar. */
  sentAt: string;
  /** The second and later attachments on one email, from 2. */
  sequence?: number;
}

/**
 * The name the document is filed under.
 *
 *   MO-900095 · Invoice · ABC Supply · 2026-10-26
 *
 * Order first because Docs lists a job's documents in one column and the
 * order is what somebody is looking for; kind next because "which invoice"
 * is the next question; supplier and date because two suppliers can bill the
 * same order and one supplier can bill it twice. An email with no order yet
 * drops the first part rather than inventing one.
 */
export function supplierDocumentTitle(
  name: SupplierDocumentName,
  timeZone = "America/Chicago",
): string {
  const parts = [
    name.orderNumber ? `MO-${name.orderNumber}` : null,
    SUPPLIER_MAIL_KIND_LABEL[name.kind],
    name.supplier?.trim() || null,
    calendarDate(name.sentAt, timeZone),
  ].filter((part): part is string => part !== null);
  const title = parts.join(" · ");
  return name.sequence && name.sequence > 1
    ? `${title} (${name.sequence})`
    : title;
}

/** YYYY-MM-DD in a time zone. An unreadable date falls back to the string. */
function calendarDate(iso: string, timeZone: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso.slice(0, 10);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** An order as far as matching needs it. */
export interface MatchableOrder {
  id: string;
  job_id: string;
  order_number: number;
  supplier_name?: string | null;
  /** The supplier's address on file (Tools → Suppliers), for `confirmSupplierSender`. */
  supplier_email?: string | null;
}

/** An attachment on the email, as far as filing needs it. */
export interface SupplierMailAttachment {
  id: string;
  filename: string;
  /** SHA-256 of the bytes, hex. How a forwarded copy is recognised. */
  content_sha256: string | null;
  /** Order numbers the reader found printed on it, if it was read. */
  order_numbers?: readonly number[] | null;
}

export interface SupplierMailPlanInput {
  classification: SupplierMailClassification;
  /** Order numbers named in the email's own text (`orderNumbersInMail`). */
  orderNumbers: readonly number[];
  attachments: readonly SupplierMailAttachment[];
  /** Every order with one of those numbers, from the database. */
  orders: readonly MatchableOrder[];
  /**
   * SHA-256 of every supplier document already filed on each job, so a
   * forwarded copy of last week's invoice is recognised and not filed twice.
   */
  filedHashes?: ReadonlyMap<string, ReadonlySet<string>>;
}

export type SupplierMailReviewReason =
  | "no_order_number"
  | "order_not_found"
  | "several_orders"
  | "unknown_kind"
  | "sender_not_supplier"
  | "sender_unverified"
  | "ship_to_no_order"
  | "ship_to_several_orders";

export interface SupplierMailFiling {
  attachmentId: string | null;
  order: MatchableOrder;
  /** True when the same bytes are already on that job: record, do not file. */
  duplicate: boolean;
}

export interface SupplierMailPlan {
  /** `filed` when every attachment found its order; `review` otherwise. */
  status: "filed" | "review";
  reasons: SupplierMailReviewReason[];
  filings: SupplierMailFiling[];
}

/**
 * Which attachment goes on which order, and what is left for a person.
 *
 * The email's own text decides for every attachment when it names exactly
 * one order. When it names several, each attachment is placed by the order
 * printed on it, if the reader found exactly one — that is how two orders in
 * one email each get their own document. Anything else is review, with the
 * attachment kept and the reason recorded.
 *
 * An unknown kind is filed if its order is certain, because the file is
 * worth more on the job than off it, and still sent to review so somebody
 * says what it is.
 */
export function planSupplierMail(
  input: SupplierMailPlanInput,
): SupplierMailPlan {
  const byNumber = new Map(input.orders.map((o) => [o.order_number, o]));
  const reasons = new Set<SupplierMailReviewReason>();
  const filings: SupplierMailFiling[] = [];

  const orderFor = (numbers: readonly number[]): MatchableOrder | null => {
    const [first, ...rest] = [...new Set(numbers)];
    if (first === undefined) {
      reasons.add("no_order_number");
      return null;
    }
    if (rest.length > 0) {
      reasons.add("several_orders");
      return null;
    }
    const order = byNumber.get(first);
    if (!order) reasons.add("order_not_found");
    return order ?? null;
  };

  const duplicateOn = (
    order: MatchableOrder,
    attachment: SupplierMailAttachment | null,
  ): boolean =>
    attachment?.content_sha256 !== null &&
    attachment?.content_sha256 !== undefined &&
    (input.filedHashes?.get(order.job_id)?.has(attachment.content_sha256) ??
      false);

  const fromText = [...new Set(input.orderNumbers)];
  if (input.attachments.length === 0) {
    const order = orderFor(fromText);
    if (order) filings.push({ attachmentId: null, order, duplicate: false });
  } else {
    for (const attachment of input.attachments) {
      const printed = [...new Set(attachment.order_numbers ?? [])];
      // The email's one order covers everything attached to it. Otherwise
      // the attachment speaks for itself; and if it says nothing, the email's
      // none-or-several is what the review reason reports.
      const numbers =
        fromText.length === 1
          ? fromText
          : printed.length > 0
            ? printed
            : fromText;
      const order = orderFor(numbers);
      if (!order) continue;
      filings.push({
        attachmentId: attachment.id,
        order,
        duplicate: duplicateOn(order, attachment),
      });
    }
  }

  const placed = new Set(filings.map((f) => f.attachmentId));
  const everythingPlaced =
    input.attachments.length === 0
      ? filings.length === 1
      : input.attachments.every((a) => placed.has(a.id));

  if (input.classification.kind === "unknown") reasons.add("unknown_kind");

  return {
    status: everythingPlaced && reasons.size === 0 ? "filed" : "review",
    reasons: [...reasons],
    filings,
  };
}

/** The sentence a review item shows for each reason. */
export function supplierMailReviewSentence(
  reason: SupplierMailReviewReason,
): string {
  switch (reason) {
    case "no_order_number":
      return "No MO number in the email or on the document.";
    case "order_not_found":
      return "Names an MO number that is not an order here.";
    case "several_orders":
      return "Names more than one order and does not say which document is which.";
    case "unknown_kind":
      return "Could not tell whether it is an acknowledgement, an invoice or a credit memo.";
    case "sender_not_supplier":
      return "Sent from an address that is not on file for that order's supplier.";
    case "sender_unverified":
      return "Says it is from that order's supplier, but the email does not prove it came from there.";
    case "ship_to_no_order":
      return "No MO number, and the Ship To address on the document is not on a job here with an order from this supplier.";
    case "ship_to_several_orders":
      return "No MO number, and the Ship To address has more than one order from this supplier, so it is not clear which.";
  }
}

/** A filed supplier document, as far as sorting needs it. */
export interface SortableSupplierDocument {
  kind: string;
  created_at: string;
  id: string;
}

/**
 * The order screen's list: acknowledgement, invoice, credit memo, then
 * anything else; newest first within each. A hand-uploaded receipt
 * (`supplier_receipt`, ADR 0145) sits with the invoices, because that is
 * what it is.
 */
export function sortSupplierDocuments<T extends SortableSupplierDocument>(
  files: readonly T[],
): T[] {
  const rank = (kind: string): number => {
    if (kind === "supplier_receipt") return 1;
    const index = SUPPLIER_MAIL_KIND_ORDER.findIndex(
      (k) => SUPPLIER_MAIL_FILE_KIND[k] === kind,
    );
    return index === -1 ? SUPPLIER_MAIL_KIND_ORDER.length : index;
  };
  return [...files].sort((a, b) => {
    const byKind = rank(a.kind) - rank(b.kind);
    if (byKind !== 0) return byKind;
    if (a.created_at !== b.created_at)
      return a.created_at < b.created_at ? 1 : -1;
    return a.id.localeCompare(b.id);
  });
}
